import { DestroyRef, Injectable, signal } from '@angular/core';
import { CommandPaletteContext } from './command.palette.types';

interface PendingOpen {
  context: CommandPaletteContext;
  resolve: () => void;
}

export type CommandPaletteContextProvider = () => CommandPaletteContext | null | undefined;

export interface CommandPaletteSettings {
  id?: string;
  context: CommandPaletteContext | CommandPaletteContextProvider;
  enabled?: () => boolean;
  priority?: number;
}

interface RegisteredCommandPaletteSettings {
  token: symbol;
  order: number;
  settings: CommandPaletteSettings;
}

interface ResolvedCommandPaletteRegistration {
  registration: RegisteredCommandPaletteSettings;
  context: CommandPaletteContext;
}

@Injectable({ providedIn: 'root' })
export class CommandPaletteService {

  private readonly _open = signal<PendingOpen | null>(null);
  private readonly _settings = signal<RegisteredCommandPaletteSettings[]>([]);
  private order = 0;

  public readonly $openContext = this._open.asReadonly();

  public setCommandK(settings: CommandPaletteSettings, destroyRef?: DestroyRef): () => void {

    const registration: RegisteredCommandPaletteSettings = { token: Symbol(settings.id ?? 'command-palette-settings'), order: this.order++, settings };
    this._settings.update(current => [...current, registration]);
    const reset = () => this.resetCommandK(registration.token);

    destroyRef?.onDestroy(reset);

    return reset;
  }

  public resetCommandK(token: symbol): void {

    this._settings.update(current => current.filter(registration => registration.token !== token));
  }

  public openCommandK(): Promise<void> {

    const resolved = this.resolveCommandKRegistration();
    if (!resolved) return Promise.resolve();

    return this.open(resolved.context);
  }

  public hasCommandKContext(): boolean {

    return this.resolveCommandKRegistration() != null;
  }

  public open(context: CommandPaletteContext): Promise<void> {

    this.close();

    return new Promise<void>(resolve => {
      this._open.set({ context, resolve });
    });
  }

  public close(): void {

    const pending = this._open();
    if (!pending) return;
    this._open.set(null);
    pending.resolve();
  }

  public isOpen(): boolean {

    return this._open() != null;
  }

  private resolveCommandKRegistration(): ResolvedCommandPaletteRegistration | null {

    const registration = [...this._settings()]
      .filter(item => item.settings.enabled?.() ?? true)
      .sort((left, right) => (right.settings.priority ?? 0) - (left.settings.priority ?? 0) || right.order - left.order)[0];

    if (!registration) return null;

    const context = typeof registration.settings.context === 'function' ? registration.settings.context() : registration.settings.context;
    if (!context?.settingsPanel) return null;

    return { registration, context };
  }
}
